import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuthStore } from "../../auth/store";
import {
  invalidateNonOrgScopedQueries,
  removeOrgScopedQueries,
} from "../../query/scope";
import { switchOrganization, type AuthTokenResponse } from "./api";

// Мутация «сделать организацию активной»
// (`POST /api/v1/me/organizations/switch`). Используется и в
// авто-switch (одна организация), и в ручном выборе (`SelectOrgPage`).
//
// На успех: ставим `orgId` в auth-store, сносим записи прежней
// организации и обновляем остальные. Обновление нужно записям
// пользователя и ничьим — их содержимое от смены организации зависит
// (`/me` с его `active_org_id`), а вот org-scoped записи трогать нельзя
// (см. комментарий в `onSuccess`).
export function useSwitchOrganization(): ReturnType<
  typeof useMutation<AuthTokenResponse, Error, string>
> {
  const queryClient = useQueryClient();
  return useMutation<AuthTokenResponse, Error, string>({
    mutationFn: (orgId) => switchOrganization(orgId),
    onSuccess: (data) => {
      // Сначала кэш, потом стор — как и в `<RequireActiveOrg>`. Порядок
      // выбран из осторожности и тестом не зафиксирован: сегодня он ни
      // на что не влияет, потому что смена стора перерисовывает
      // потребителей не синхронно, и к моменту сноса запросов новой
      // организации ещё нет. Разойдётся это, если между строками
      // появится точка приостановки — тогда снос убил бы уже заведённые
      // запросы новой организации.
      //
      // Одного `invalidateQueries()` НЕДОСТАТОЧНО для `["me", userId]`:
      // без фильтра он использует дефолтный `refetchType: "active"`, а
      // в момент switch'а запрос `useMe()` неактивен — мутация
      // выполняется со страницы `/select-org` (`SelectOrgPage`),
      // которая сама `useMe()` не вызывает, поэтому ни один его
      // потребитель ещё не смонтирован. Неактивный запрос invalidate
      // лишь помечает stale, НЕ рефетчит — он продолжает синхронно
      // отдавать протухшие данные (`active_org_id` ДО switch'а) до
      // следующего фонового рефетча.
      //
      // На следующей защищённой странице `RequireActiveOrg` берёт
      // быстрый путь по свежему `orgId` из стора (уже проставлен
      // строкой выше), но его `useEffect` всё равно читает `useMe()`.
      // Тот синхронно вернёт протухший кэш → эффект решит, что стор
      // разошёлся с сервером, и откатит `orgId` обратно на
      // `null` (`setActiveOrg(null)`) РАНЬШЕ, чем фоновый рефетч
      // успеет прийти. Итог без фикса — повторный редирект на
      // `/select-org` и второй авто-switch (два `POST .../switch` на
      // один вход).
      //
      // `removeQueries()` убирает протухшую запись из кэша целиком
      // (не просто помечает stale): следующий потребитель `useMe()`
      // увидит `isPending` (данных нет вообще) и дождётся свежего
      // ответа, а не отработает по протухшему значению. `queryKey:
      // ["me"]` — префиксный матч, ловит `["me", userScopeSegment]` (см.
      // `useMe.ts`), не завязываясь на конкретный сегмент.
      //
      // Org-scoped ключи (см. `src/query/scope.ts`) несут саму
      // организацию: после смены записи прежней организации попадают под
      // другой ключ, и обновлять их незачем. Обновление им не просто
      // лишнее, а вредное: безфильтровая инвалидация СИНХРОННО рефетчит
      // активные запросы, а те до перерендера всё ещё держат ключ
      // прежней организации — ответ новой лёг бы под чужой ключ.
      //
      // Тот же ответ под чужим ключом даёт запрос, ушедший за миг ДО
      // смены: он резолвится уже данными новой организации. Инвалидация
      // тут бессильна, поэтому записи прежней организации сносим —
      // они всё равно больше не нужны, а прожили бы `gcTime`.
      queryClient.removeQueries({ queryKey: ["me"] });
      removeOrgScopedQueries(queryClient);
      invalidateNonOrgScopedQueries(queryClient);
      useAuthStore.getState().setActiveOrg(data.org_id);
    },
  });
}
